/**
 * i18n - 多語言支援
 * 支援繁體中文、簡體中文、英文
 */

export type Language = 'zh-TW' | 'zh-CN' | 'en';

export interface Translations {
  // Header
  appTitle: string;
  appSubtitle: string;
  
  // Categories
  categoryVps: string;
  categoryVds: string;
  categoryHosting: string;
  categoryAccount: string;
  categoryDomain: string;
  
  // Basic Info Section
  basicInfo: string;
  provider: string;
  registrar: string;
  productName: string;
  domainName: string;
  region: string;
  remarks: string;
  
  // Specs Section
  specsConfig: string;
  cpu: string;
  ram: string;
  diskConfig: string;
  disk: string;
  addDisk: string;
  routeType: string;
  custom: string;
  
  // Network Section
  networkConfig: string;
  unlimitedTraffic: string;
  traffic: string;
  unit: string;
  direction: string;
  portSpeed: string;
  speedUnit: string;
  throttleInfo: string;
  directionBoth: string;
  directionIn: string;
  directionOut: string;
  
  // Finance Section
  priceInfo: string;
  renewalPrice: string;
  currency: string;
  billingCycle: string;
  purchaseDate: string;
  dueDate: string;
  lifetime: string;
  sellingPrice: string;
  exchangeRate: string;
  targetCurrency: string;
  monthly: string;
  yearly: string;
  twoYears: string;
  
  // Contact Section
  contactInfo: string;
  telegramId: string;
  nodeseekId: string;
  tradingPlatform: string;
  
  // Card Preview
  remainingTime: string;
  lifetimeValid: string;
  days: string;
  residualValue: string;
  price: string;
  premium: string;
  discount: string;
  
  // Actions
  copyText: string;
  downloadImage: string;
  copied: string;
  downloading: string;
  
  // Domain/Account specific
  domainInfo: string;
  accountInfo: string;
  platform: string;
  product: string;
}

const zhTW: Translations = {
  appTitle: 'MJJ 交易工具',
  appSubtitle: 'VPS/域名交易卡片生成器',
  categoryVps: 'VPS',
  categoryVds: 'VDS/獨服',
  categoryHosting: '虛擬主機',
  categoryAccount: '帳號/會員',
  categoryDomain: '域名',
  basicInfo: '基礎資訊',
  provider: '廠商',
  registrar: '註冊商',
  productName: '產品名稱',
  domainName: '域名',
  region: '地區',
  remarks: '備註',
  specsConfig: '規格配置',
  cpu: 'CPU',
  ram: 'RAM',
  diskConfig: '硬碟配置',
  disk: '硬碟',
  addDisk: '+ 新增硬碟',
  routeType: '線路類型',
  custom: '自定義',
  networkConfig: '網路配置',
  unlimitedTraffic: '無限流量',
  traffic: '流量',
  unit: '單位',
  direction: '計算方向',
  portSpeed: '端口速率',
  speedUnit: '速率單位',
  throttleInfo: '限速說明（選填）',
  directionBoth: '雙向計算',
  directionIn: '只算入站',
  directionOut: '只算出站',
  priceInfo: '價格資訊',
  renewalPrice: '續費價格',
  currency: '幣種',
  billingCycle: '計費週期',
  purchaseDate: '購買日期',
  dueDate: '到期日',
  lifetime: '終身',
  sellingPrice: '預期售價',
  exchangeRate: '匯率',
  targetCurrency: '目標幣種',
  monthly: '月付',
  yearly: '年付',
  twoYears: '兩年付',
  contactInfo: '聯繫方式',
  telegramId: 'Telegram ID',
  nodeseekId: 'Nodeseek ID',
  tradingPlatform: '交易平台連結',
  remainingTime: '剩餘時間',
  lifetimeValid: '終身有效',
  days: '天',
  residualValue: '剩餘價值',
  price: '售價',
  premium: '溢價',
  discount: '折價',
  copyText: '複製文案',
  downloadImage: '下載圖片',
  copied: '已複製！',
  downloading: '下載中...',
  domainInfo: '域名資訊',
  accountInfo: '帳號資訊',
  platform: '平台',
  product: '產品',
};

const zhCN: Translations = {
  appTitle: 'MJJ 交易工具',
  appSubtitle: 'VPS/域名交易卡片生成器',
  categoryVps: 'VPS',
  categoryVds: 'VDS/独服',
  categoryHosting: '虚拟主机',
  categoryAccount: '账号/会员',
  categoryDomain: '域名',
  basicInfo: '基础信息',
  provider: '厂商',
  registrar: '注册商',
  productName: '产品名称',
  domainName: '域名',
  region: '地区',
  remarks: '备注',
  specsConfig: '规格配置',
  cpu: 'CPU',
  ram: 'RAM',
  diskConfig: '硬盘配置',
  disk: '硬盘',
  addDisk: '+ 新增硬盘',
  routeType: '线路类型',
  custom: '自定义',
  networkConfig: '网络配置',
  unlimitedTraffic: '无限流量',
  traffic: '流量',
  unit: '单位',
  direction: '计算方向',
  portSpeed: '端口速率',
  speedUnit: '速率单位',
  throttleInfo: '限速说明（选填）',
  directionBoth: '双向计算',
  directionIn: '只算入站',
  directionOut: '只算出站',
  priceInfo: '价格信息',
  renewalPrice: '续费价格',
  currency: '币种',
  billingCycle: '计费周期',
  purchaseDate: '购买日期',
  dueDate: '到期日',
  lifetime: '终身',
  sellingPrice: '预期售价',
  exchangeRate: '汇率',
  targetCurrency: '目标币种',
  monthly: '月付',
  yearly: '年付',
  twoYears: '两年付',
  contactInfo: '联系方式',
  telegramId: 'Telegram ID',
  nodeseekId: 'Nodeseek ID',
  tradingPlatform: '交易平台链接',
  remainingTime: '剩余时间',
  lifetimeValid: '终身有效',
  days: '天',
  residualValue: '剩余价值',
  price: '售价',
  premium: '溢价',
  discount: '折价',
  copyText: '复制文案',
  downloadImage: '下载图片',
  copied: '已复制！',
  downloading: '下载中...',
  domainInfo: '域名信息',
  accountInfo: '账号信息',
  platform: '平台',
  product: '产品',
};

const en: Translations = {
  appTitle: 'MJJ Trading Tool',
  appSubtitle: 'VPS/Domain Trading Card Generator',
  categoryVps: 'VPS',
  categoryVds: 'VDS/Dedicated',
  categoryHosting: 'Hosting',
  categoryAccount: 'Account',
  categoryDomain: 'Domain',
  basicInfo: 'Basic Info',
  provider: 'Provider',
  registrar: 'Registrar',
  productName: 'Product Name',
  domainName: 'Domain',
  region: 'Region',
  remarks: 'Remarks',
  specsConfig: 'Specifications',
  cpu: 'CPU',
  ram: 'RAM',
  diskConfig: 'Disk Config',
  disk: 'Disk',
  addDisk: '+ Add Disk',
  routeType: 'Route Type',
  custom: 'Custom',
  networkConfig: 'Network Config',
  unlimitedTraffic: 'Unlimited Traffic',
  traffic: 'Traffic',
  unit: 'Unit',
  direction: 'Direction',
  portSpeed: 'Port Speed',
  speedUnit: 'Speed Unit',
  throttleInfo: 'Throttle Info (Optional)',
  directionBoth: 'Bidirectional',
  directionIn: 'Inbound Only',
  directionOut: 'Outbound Only',
  priceInfo: 'Price Info',
  renewalPrice: 'Renewal Price',
  currency: 'Currency',
  billingCycle: 'Billing Cycle',
  purchaseDate: 'Purchase Date',
  dueDate: 'Due Date',
  lifetime: 'Lifetime',
  sellingPrice: 'Selling Price',
  exchangeRate: 'Exchange Rate',
  targetCurrency: 'Target Currency',
  monthly: 'Monthly',
  yearly: 'Yearly',
  twoYears: '2 Years',
  contactInfo: 'Contact',
  telegramId: 'Telegram ID',
  nodeseekId: 'Nodeseek ID',
  tradingPlatform: 'Trading Platform',
  remainingTime: 'Remaining',
  lifetimeValid: 'Lifetime',
  days: 'days',
  residualValue: 'Residual Value',
  price: 'Price',
  premium: 'Premium',
  discount: 'Discount',
  copyText: 'Copy Text',
  downloadImage: 'Download Image',
  copied: 'Copied!',
  downloading: 'Downloading...',
  domainInfo: 'Domain Info',
  accountInfo: 'Account Info',
  platform: 'Platform',
  product: 'Product',
};

export const translations: Record<Language, Translations> = {
  'zh-TW': zhTW,
  'zh-CN': zhCN,
  'en': en,
};

export const languageNames: Record<Language, string> = {
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  'en': 'English',
};

export function getTranslations(lang: Language): Translations {
  return translations[lang];
}
